import React, { useState, useMemo } from 'react';
import '../../styles/v4-theme.css';

/**
 * MasterTimeline Component
 * Displays chronological timeline of all events across all targets
 */
const MasterTimeline = ({ timeline = [], onEventClick, onTimestampClick }) => {
  const [filterType, setFilterType] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);

  // Handle timestamp click
  const handleTimestampClick = (e, timestamp, event) => {
    e.stopPropagation();
    if (onTimestampClick) {
      onTimestampClick(timestamp, event);
    }
  };

  // Get unique event types
  const eventTypes = useMemo(() => {
    const types = new Set(['all']);
    timeline.forEach(event => {
      if (event.target_type) {
        types.add(event.target_type);
      }
    });
    return Array.from(types);
  }, [timeline]);

  // Filter events
  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return timeline;
    return timeline.filter(event => event.target_type === filterType);
  }, [timeline, filterType]);

  // Get icon for target type
  const getTypeIcon = (targetType) => {
    const icons = {
      vehicle: '◆',
      person: '●',
      environment: '▲',
      videographer: '■',
      text: '⊞',
      weapon: '▼',
      object: '◇'
    };
    return icons[targetType] || '▸';
  };

  // Get type color class
  const getTypeClass = (targetType) => {
    return `type-${targetType || 'vehicle'}`;
  };

  // Format event title
  const getEventTitle = (event) => {
    if (event.event_type) {
      return event.event_type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return event.event || 'Event';
  };

  if (timeline.length === 0) {
    return (
      <div className="v4-master-timeline">
        <div className="v4-empty-state">
          <div className="v4-empty-state-icon">📅</div>
          <div className="v4-empty-state-title">No Timeline Events</div>
          <div className="v4-empty-state-description">
            No timeline events were recorded for this analysis.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="v4-master-timeline">
      {/* Header */}
      <div className="v4-timeline-header">
        <div className="v4-timeline-title">
          📅 Master Timeline - All Events ({filteredEvents.length})
        </div>
        <div className="v4-timeline-controls">
          <button
            className="v4-timeline-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '▼ Collapse' : '▶ Expand'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Filter Buttons */}
          {eventTypes.size > 1 && (
            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-4)',
              flexWrap: 'wrap'
            }}>
              {Array.from(eventTypes).map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className="v4-timeline-button"
                  style={{
                    background: filterType === type ? 'var(--accent)' : 'var(--bg-card)',
                    color: filterType === type ? 'white' : 'var(--text-secondary)',
                    border: `1px solid ${filterType === type ? 'var(--accent)' : 'var(--border)'}`
                  }}
                >
                  {getTypeIcon(type)} {type === 'all' ? 'All' : type}
                </button>
              ))}
            </div>
          )}

          {/* Events List */}
          <div className="v4-timeline-events">
            {filteredEvents.map((event, idx) => (
              <div
                key={event.event_id || idx}
                className={`v4-timeline-event ${getTypeClass(event.target_type)}`}
                onClick={() => onEventClick && onEventClick(event)}
              >
                {/* Event Header */}
                <div className="v4-timeline-event-header">
                  <div 
                    className={`v4-timeline-event-time ${onTimestampClick ? 'v4-timestamp-link' : ''}`}
                    onClick={(e) => onTimestampClick && handleTimestampClick(e, event.timestamp_display || event.timestamp, event)}
                    title={onTimestampClick ? "Click to jump to video" : undefined}
                  >
                    {event.timestamp_display || event.timestamp || '00:00:00'}
                  </div>
                  <div className="v4-timeline-event-icon">
                    {getTypeIcon(event.target_type)}
                  </div>
                  <div className="v4-timeline-event-title">
                    {getEventTitle(event)}
                  </div>
                </div>

                {/* Event Description */}
                {event.description && (
                  <div className="v4-timeline-event-description">
                    {event.description}
                  </div>
                )}

                {/* Event Metadata */}
                {event.target_id && (
                  <div style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 'var(--space-2)',
                    paddingLeft: 'calc(var(--space-3) * 6)',
                    fontFamily: 'monospace'
                  }}>
                    Target: {event.target_id}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div style={{
            marginTop: 'var(--space-4)',
            textAlign: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)'
          }}>
            Showing {filteredEvents.length} of {timeline.length} events
          </div>
        </>
      )}
    </div>
  );
};

export default MasterTimeline;

